import React, { useRef, useEffect } from 'react';
import { Line } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    ChartOptions
} from 'chart.js';
import { CHART_CONFIG } from '@core/constants/chartConfig.ts';

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

interface LineChartProps {
    dataSet?: number[];
    lineTension?: number;
    dataLabels?: string[];
    gradientColor1?: string;
    gradientColor2?: string;
    enableGradient?: boolean;
    borderColor?: string;
    style?: React.CSSProperties;
}

const LineChart: React.FC<LineChartProps> = ({
    dataSet = [10, 30, 30, 60, 75, 10],
    lineTension = 0.3,
    dataLabels = ['A', 'B', 'C', 'D', 'E', 'F'],
    gradientColor1 = CHART_CONFIG.color.white,
    gradientColor2 = CHART_CONFIG.color.white,
    enableGradient = true,
    borderColor = CHART_CONFIG.color.white,
    style
}) => {
    const chartRef = useRef<ChartJS<'line', number[], string>>(null);

    useEffect(() => {
        const chart = chartRef.current;
        if (chart?.ctx) {
            const ctx = chart.ctx;

            // Store the original stroke method
            const originalStroke = ctx.stroke.bind(ctx);

            // Override stroke method to add shadow effect
            ctx.stroke = function () {
                ctx.save();
                ctx.shadowColor = 'rgba(0,0,0,0.5)';
                ctx.shadowBlur = 8;
                ctx.shadowOffsetX = 0;
                ctx.shadowOffsetY = 4;
                originalStroke();
                ctx.restore();
            };
        }
    }, []);

    const createGradient = (): string | CanvasGradient => {
        const chart = chartRef.current;
        if (!chart?.ctx || !enableGradient) return borderColor;

        const ctx = chart.ctx;
        const gradient = ctx.createLinearGradient(0, 0, 250, 0);
        gradient.addColorStop(0, gradientColor1);
        gradient.addColorStop(1, gradientColor2);
        return gradient;
    };

    const options: ChartOptions<'line'> = {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            y: {
                beginAtZero: true,
                display: false,
                grid: {
                    display: false
                },
                border: {
                    display: false
                }
            },
            x: {
                display: false,
                beginAtZero: true,
                grid: {
                    display: true
                },
                border: {
                    display: false
                }
            }
        },
        plugins: {
            legend: {
                display: false
            }
        },
        elements: {
            point: {
                borderWidth: 0,
                hoverRadius: 0,
                radius: 0,
                hoverBorderWidth: 0
            },
            line: {
                borderWidth: 3,
                tension: lineTension,
                fill: false
            }
        }
    };

    const data = {
        labels: dataLabels,
        datasets: [
            {
                label: 'My First dataset',
                data: dataSet,
                borderColor: createGradient(),
                backgroundColor: 'transparent'
            }
        ]
    };

    return (
        <div style={style}>
            <Line ref={chartRef} data={data} options={options} />
        </div>
    );
};

export default LineChart;
